import { z } from 'zod';
import { roundToCents } from '../money-math';
import type { QuoteCalcResult } from './quote-calculation-engine';
import {
  computeOfferCalcRows,
  DEFAULT_OFFER_LOGO,
  offerCustomTermSchema,
  offerFlowItemSchema,
  offerLogoLayoutSchema,
  offerPriceSummarySchema,
  offerSpecExtraSchema,
  offerTechRowSchema,
  type OfferCalcRow,
  type OfferFlowItem,
  type OfferLogoLayout,
  type OfferSpecExtra,
} from './quote-offer-blocks';
import {
  localizedTextSchema,
  microsStringSchema,
  QUOTE_CALC_GROUP_KEYS,
  quoteCalcGroupKeySchema,
  type LocalizedText,
  type QuoteCalcFormulaRow,
  type QuoteCalcGroupText,
  type QuoteCalcLine,
  type QuoteCalcPlant,
  type QuoteCalculationResponse,
} from './quote-calculation.schema';

/**
 * Angebot zur Kalkulation (ADR 0112, Schritt 4): Aufbau, Texte, Datenblatt-Seiten
 * und Konditionen. Gespeichert wird nur, was das Angebot von der Kalkulation
 * unterscheidet — Mengen und Preise kommen immer live aus der Kalkulation, `null`
 * heißt „aus der Kalkulation". Vorschau im Browser und PDF des Servers zeichnen
 * beide dasselbe Druckmodell (`buildQuoteOfferDocument`).
 *
 * Mehrere Anlagen: je Anlage eine Seite im bekannten Layout (Untertitel, technische
 * Daten, ihre Gruppen) und direkt dahinter ihre Spezifikationen; danach eine
 * Zusammenfassung mit den gemeinsamen Leistungen und den Preisen. Mit einer Anlage
 * sieht das Angebot aus wie bisher.
 */

export type OfferLanguage = 'de' | 'en';

export const OFFER_TECH_FIELDS = ['lengthM', 'widthM', 'heightM', 'waterLevelM', 'flightSpacingM', 'scrapingSpeedCmS', 'basinCount'] as const;
export type OfferTechField = (typeof OFFER_TECH_FIELDS)[number];
export const OFFER_TECH_LABELS: Record<OfferTechField, LocalizedText> = {
  lengthM: { de: 'Räumlänge (m)', en: 'Length (m)' },
  widthM: { de: 'Räumbreite (m)', en: 'Width (m)' },
  heightM: { de: 'Beckenhöhe (m)', en: 'Basin height (m)' },
  waterLevelM: { de: 'Wassertiefe (m)', en: 'Depth (m)' },
  flightSpacingM: { de: 'Balkenabstand (m)', en: 'Flight distance (m)' },
  scrapingSpeedCmS: { de: 'Räumgeschwindigkeit (cm/s)', en: 'Scraping velocity (cm/s)' },
  basinCount: { de: 'Anzahl Becken', en: 'Number of basins' },
};

export const OFFER_TERM_KEYS = ['priceBasis', 'delivery', 'validUntil', 'deliveryTime', 'payment', 'warranty', 'other'] as const;
export type OfferTermKey = (typeof OFFER_TERM_KEYS)[number];
export const OFFER_TERM_LABELS: Record<OfferTermKey, LocalizedText> = {
  priceBasis: { de: 'Preisbasis', en: 'Price Basis' },
  delivery: { de: 'Lieferung', en: 'Delivery' },
  validUntil: { de: 'Gültigkeit', en: 'Validity' },
  deliveryTime: { de: 'Lieferzeit', en: 'Delivery Time' },
  payment: { de: 'Zahlungsbedingungen', en: 'Payment Terms' },
  warranty: { de: 'Gewährleistung', en: 'Warranty' },
  other: { de: 'Sonstiges', en: 'Other' },
};

/** Texte des Blatts, die nicht bearbeitet werden — wie in den bisherigen PROBIG-Angeboten. */
const LABEL_KEYS = ['offerNo', 'date', 'specifications', 'terms', 'totalOptions', 'tagline', 'totalLength', 'totalAmount', 'flightLength', 'summary', 'price', 'common', 'continued'] as const;
export const OFFER_LABELS: Record<OfferLanguage, Record<(typeof LABEL_KEYS)[number], string>> = {
  de: {
    offerNo: 'Angebotsnr.:',
    date: 'Datum:',
    specifications: 'Spezifikationen',
    terms: 'Angebotsbedingungen',
    totalOptions: 'Summe Optionen',
    tagline: '',
    totalLength: 'Gesamtlänge (m)',
    totalAmount: 'Gesamtanzahl',
    flightLength: 'Balkenlänge (mm)',
    summary: 'Zusammenfassung',
    price: 'Preis',
    common: 'Gemeinsame Komponenten',
    continued: '(Fortsetzung)',
  },
  en: {
    offerNo: 'Offer No.:',
    date: 'Date:',
    specifications: 'Specifications',
    terms: 'Terms and Conditions',
    totalOptions: 'Total options',
    tagline: 'NON-METALLIC SCRAPER SYSTEMS',
    totalLength: 'Total length (m)',
    totalAmount: 'Total amount',
    flightLength: 'Flight length (mm)',
    summary: 'Summary',
    price: 'Price',
    common: 'Common components',
    continued: '(continued)',
  },
};

const lineIdSchema = z.string().trim().min(1).max(64);
const isoDateSchema = z.string().regex(/^\d{4}-\d{2}-\d{2}$/, 'Datum als JJJJ-MM-TT erwartet');
/** Merkmal-Zeile; `isTotal` = hervorgehoben (fett, blau hinterlegt). */
const specRowsSchema = z.array(z.object({ label: z.string().max(1000), value: z.string().max(2000), isTotal: z.boolean().optional() })).max(60);

export const quoteOfferGroupSchema = z.object({
  key: quoteCalcGroupKeySchema,
  isVisible: z.boolean(),
  title: localizedTextSchema,
  /** Pauschal: statt der Positionen steht nur `text` im Angebot. */
  isLumpSum: z.boolean(),
  /** Pauschaltext bzw. Zusatztext unter den Positionen. */
  text: localizedTextSchema,
  /** Preis der Gruppe in der Überschrift zeigen (z. B. Stahlkomponenten „€ 16.140"). */
  showPrice: z.boolean().optional(),
  lines: z
    .array(
      z.object({
        lineId: lineIdSchema,
        isVisible: z.boolean(),
        /** Gesamtmenge im Angebot von Hand; null = Kalkulation × Becken. */
        quantity: z.number().finite().min(0).max(100_000_000).nullable(),
        /** Text von Hand; null = Angebotstext der Position. */
        text: localizedTextSchema.nullable(),
        /** Einheit von Hand; null/fehlt = Einheit der Position. */
        unit: z.string().trim().max(20).nullable().optional(),
        /** Seitenumbruch vor dieser Position. */
        breakBefore: z.boolean().optional(),
        /** Nur im Angebot angelegte Position (ohne Preis, nicht in der Kalkulation). */
        isCustom: z.boolean().optional(),
        /** Anlage der eigenen Position; null = ganzes Angebot. */
        plantId: z.string().max(64).nullable().optional(),
      }),
    )
    .max(600),
});
export type QuoteOfferGroup = z.infer<typeof quoteOfferGroupSchema>;

export const quoteOfferOptionItemSchema = z.discriminatedUnion('kind', [
  z.object({
    kind: z.literal('line'),
    lineId: lineIdSchema,
    isVisible: z.boolean(),
    title: localizedTextSchema.nullable(),
    text: localizedTextSchema.nullable(),
    /** Menge im Angebot von Hand, z. B. „2 Stk." — null/fehlt = aus der Kalkulation. */
    quantity: z.string().trim().max(40).nullable().optional(),
    /** Preis von Hand — null/fehlt = Angebotswert der Kalkulation. Gilt auch für den Auftrag. */
    amountMicros: microsStringSchema.nullable().optional(),
    breakBefore: z.boolean().optional(),
  }),
  z.object({ kind: z.literal('heading'), id: lineIdSchema, isVisible: z.boolean(), title: localizedTextSchema, breakBefore: z.boolean().optional() }),
  /** Option nur im Angebot (nicht in der Kalkulation): Menge, Text und Preis von Hand. */
  z.object({
    kind: z.literal('custom'),
    id: lineIdSchema,
    isVisible: z.boolean(),
    quantity: z.string().trim().max(40),
    title: localizedTextSchema,
    text: localizedTextSchema,
    amountMicros: microsStringSchema,
    breakBefore: z.boolean().optional(),
  }),
  z.object({ kind: z.literal('text'), id: lineIdSchema, isVisible: z.boolean(), text: localizedTextSchema, breakBefore: z.boolean().optional() }),
  offerPriceSummarySchema,
]);
export type QuoteOfferOptionItem = z.infer<typeof quoteOfferOptionItemSchema>;

export const quoteOfferSpecCardSchema = z.object({
  isHidden: z.boolean(),
  title: localizedTextSchema.nullable(),
  /** Merkmale von Hand; null = aus der Position (Artikel). */
  rows: z.object({ de: specRowsSchema, en: specRowsSchema }).nullable(),
});
export type QuoteOfferSpecCard = z.infer<typeof quoteOfferSpecCardSchema>;

/** Satz Spezifikationsseiten: je Anlage (ID) bzw. „project" für Gruppen „ganzes Angebot". */
export const OFFER_PROJECT_SPEC_SET = 'project';
const specPagesSchema = z.array(z.array(lineIdSchema).max(40)).min(1).max(30);

export const quoteOfferSchema = z.object({
  /** Angebotsdatum (JJJJ-MM-TT). */
  date: isoDateSchema,
  /** Titel von Hand; null/fehlt = Name der Kalkulation. */
  title: localizedTextSchema.nullable().optional(),
  /** Untertitel je Anlage von Hand (Schlüssel = Anlagen-ID); fehlt = aus der Kalkulation. */
  plantSubtitles: z.record(z.string().min(1).max(64), localizedTextSchema).optional(),
  /** Größe und Lage des Logos; fehlt = 44 mm rechts. */
  logo: offerLogoLayoutSchema.optional(),
  techFields: z.array(z.enum(OFFER_TECH_FIELDS)).max(OFFER_TECH_FIELDS.length),
  /** Technische Angaben von Hand: Wert bzw. Bezeichnung je Feld (fehlt = aus der Kalkulation). */
  techValues: z.record(z.enum(OFFER_TECH_FIELDS), z.string().max(200)).optional(),
  techLabels: z.record(z.enum(OFFER_TECH_FIELDS), localizedTextSchema).optional(),
  /** Eigene Zeilen bei den technischen Daten. */
  techRows: z.array(offerTechRowSchema).max(20).optional(),
  /** Reihenfolge der Angebotsseite (Gruppen + Bausteine); fehlt = Gruppen in `groups`-Reihenfolge. */
  flow: z.array(offerFlowItemSchema).max(120).optional(),
  /** Seitenumbruch vor dem Gesamtpreis. */
  totalBreakBefore: z.boolean().optional(),
  groups: z.array(quoteOfferGroupSchema).max(QUOTE_CALC_GROUP_KEYS.length),
  total: z.object({ label: localizedTextSchema, priceMicros: microsStringSchema.nullable() }),
  /** Bei mehreren Anlagen: Preis je Anlage auf deren Seite und in der Zusammenfassung zeigen. */
  showPlantPrices: z.boolean().optional(),
  /** Logo: null = Logo der Gesellschaft, sonst ein hochgeladenes Logo (QuoteCalcLogo). */
  logoId: z.string().uuid().nullable().optional(),
  options: z.object({
    isVisible: z.boolean(),
    showTotal: z.boolean(),
    title: localizedTextSchema,
    items: z.array(quoteOfferOptionItemSchema).max(300),
  }),
  specs: z.object({
    isVisible: z.boolean(),
    /** Seiten vor den Anlagen (ein Satz); beim Lesen in `sets` überführt. */
    pages: specPagesSchema.optional(),
    sets: z.record(z.string().min(1).max(64), specPagesSchema).optional(),
    cards: z.record(lineIdSchema, quoteOfferSpecCardSchema),
    /** Eigene Einträge (Karte, Überschrift, Text); ihre ID steht wie eine Position in den Seiten. */
    extras: z.record(lineIdSchema, offerSpecExtraSchema).optional(),
  }),
  terms: z.object({
    priceBasis: localizedTextSchema,
    delivery: localizedTextSchema,
    validUntil: isoDateSchema.nullable(),
    deliveryTime: localizedTextSchema,
    payment: localizedTextSchema,
    warranty: localizedTextSchema,
    other: localizedTextSchema,
    /** Eigene Konditionen. */
    custom: z.array(offerCustomTermSchema).max(30).optional(),
    /** Reihenfolge (Schlüssel bzw. IDs der eigenen); fehlt = feste Reihenfolge, eigene dahinter. */
    order: z.array(z.string().min(1).max(64)).max(60).optional(),
    /** Ausgeblendete feste Konditionen. */
    hidden: z.array(z.enum(OFFER_TERM_KEYS)).max(OFFER_TERM_KEYS.length).optional(),
  }),
});
export type QuoteOffer = z.infer<typeof quoteOfferSchema>;

export const quoteOfferResponseSchema = z.object({
  offer: quoteOfferSchema,
  /** false = noch nie gespeichert, aus der Kalkulation vorgeschlagen. */
  isSaved: z.boolean(),
  legalEntityId: z.string().uuid().nullable(),
  updatedAt: z.string().datetime(),
});
export type QuoteOfferResponse = z.infer<typeof quoteOfferResponseSchema>;

export const saveQuoteOfferSchema = z.object({
  offer: quoteOfferSchema,
  legalEntityId: z.string().uuid().nullable(),
  /** Stand der Kalkulation, den der Client gesehen hat (409 bei fremder Änderung). */
  expectedUpdatedAt: z.string().datetime(),
});
export type SaveQuoteOfferInput = z.infer<typeof saveQuoteOfferSchema>;

/** Logos für Angebote: zusätzlich zum Logo der Gesellschaft, je Gesellschaft oder nur für ein Angebot. */
export const quoteCalcLogoSchema = z.object({
  id: z.string().uuid(),
  name: z.string(),
  legalEntityId: z.string().uuid().nullable(),
  /** Nur für dieses Angebot (Kalkulation); null = für alle Angebote der Gesellschaft. */
  calculationId: z.string().uuid().nullable(),
  mimeType: z.string(),
  dataUrl: z.string(),
});
export type QuoteCalcLogo = z.infer<typeof quoteCalcLogoSchema>;
export const uploadQuoteCalcLogoSchema = z.object({
  name: z.string().trim().min(1).max(60),
  legalEntityId: z.string().uuid().nullable(),
  calculationId: z.string().uuid().nullable(),
  /** data:image/png;base64,… — PNG oder JPEG, höchstens 1 MB. */
  dataUrl: z.string().regex(/^data:image\/(png|jpeg);base64,[A-Za-z0-9+/=]+$/, 'Logo als PNG oder JPG erwartet (SVG kann das PDF nicht zeichnen)').max(1_400_000, 'Logo höchstens 1 MB'),
});
export type UploadQuoteCalcLogoInput = z.infer<typeof uploadQuoteCalcLogoSchema>;

/** Was das Angebot von der Kalkulation braucht. */
export type QuoteOfferCalc = Pick<
  QuoteCalculationResponse,
  | 'number' | 'displayNumber' | 'name' | 'subtitle' | 'scraperType' | 'basinCount' | 'lengthM' | 'widthM'
  | 'heightM' | 'waterLevelM' | 'flightSpacingM' | 'scrapingSpeedCmS' | 'flightCountOverride' | 'flightLengthOverrideM'
  | 'finalPriceMicros' | 'validUntil' | 'createdAt' | 'groups' | 'plants'
>;
export type QuoteOfferTemplates = { formulas: readonly QuoteCalcFormulaRow[]; groupTexts: readonly QuoteCalcGroupText[] };

// ---------- Vorschlag aus der Kalkulation ----------

const both = (text: string): LocalizedText => ({ de: text, en: text });

/** Konditionen, wie sie in den meisten Angeboten der alten App standen. */
const DEFAULT_TERMS: Omit<QuoteOffer['terms'], 'validUntil'> = {
  priceBasis: {
    de: 'Die angegebenen Festpreise verstehen sich zzgl. MwSt. bei Abnahme des angebotenen Umfangs. Es gelten unsere Verkaufs- und Lieferbedingungen',
    en: 'The stated fixed prices are plus VAT when purchasing the offered scope. Our terms of sale and delivery apply',
  },
  delivery: { de: 'DAP', en: 'DAP' },
  deliveryTime: {
    de: 'ca. 12 - 16 Wochen nach Auftragserteilung und Planfreigabe',
    en: 'approx. 12 weeks after agreement and plan approval',
  },
  payment: {
    de: '30% - bei Auftragserteilung\n65% - bei Lieferung\n5% - nach Montage (spätestens 3 Monate nach Lieferung)',
    en: '30% - upon order\n65% - upon delivery\n5% - after installation (no later than 3 months after delivery)',
  },
  warranty: { de: '24 Monate nach Lieferung', en: '24 months after delivery' },
  other: {
    de: 'Im Angebot nicht enthalten, sofern nicht ausdrücklich angeführt:\nMontage, Elektroarbeiten, Kran, Gerüst, Bauarbeiten, Strom, Wasser und sonstige Nebenleistungen.',
    en: 'not included in the offer unless expressly stated:\nassembly, electrical work, crane, scaffold, construction work, electricity, water and other ancillary services.',
  },
};

/** Datenblatt-Karten je Seite im Vorschlag — vier passen auf ein A4-Blatt. */
const SPEC_CARDS_PER_PAGE = 4;

function groupText(templates: QuoteOfferTemplates, calc: QuoteOfferCalc, key: string): QuoteCalcGroupText | undefined {
  const texts = templates.groupTexts.filter((text) => text.group === key);
  const type = calc.plants[0]?.scraperType ?? calc.scraperType;
  return texts.find((text) => text.scraperType === type) ?? texts.find((text) => text.scraperType === null);
}

const hasSpecs = (line: QuoteCalcLine) => Boolean(line.specs && (line.specs.de.length || line.specs.en.length));
const isMainGroup = (key: string) => key !== 'OPTIONS';
/** Satz, in den die Spezifikation einer Position gehört. */
const specSetOf = (line: QuoteCalcLine) => line.plantId ?? OFFER_PROJECT_SPEC_SET;

function defaultGroup(calc: QuoteOfferCalc, templates: QuoteOfferTemplates, group: QuoteCalculationResponse['groups'][number]): QuoteOfferGroup {
  const text = groupText(templates, calc, group.key);
  const isLumpSum = text?.isLumpSum ?? false;
  return {
    key: group.key,
    isVisible: group.lines.length > 0 && (isLumpSum || group.lines.some((line) => line.showInOffer)),
    title: text?.title ?? both(group.title),
    isLumpSum,
    text: text?.text ?? both(''),
    showPrice: false,
    lines: group.lines.map((line) => ({ lineId: line.id, isVisible: line.showInOffer, quantity: null, text: null })),
  };
}

const chunk = (ids: string[]): string[][] => {
  const pages: string[][] = [];
  for (let index = 0; index < ids.length; index += SPEC_CARDS_PER_PAGE) pages.push(ids.slice(index, index + SPEC_CARDS_PER_PAGE));
  return pages.length ? pages : [[]];
};

function defaultSpecSets(calc: QuoteOfferCalc): Record<string, string[][]> {
  const lines = calc.groups.filter((group) => isMainGroup(group.key)).flatMap((group) => group.lines.filter((line) => line.showInSpecs && hasSpecs(line)));
  const keys = [...calc.plants.map((plant) => plant.id), OFFER_PROJECT_SPEC_SET];
  return Object.fromEntries(keys.map((key) => [key, chunk(lines.filter((line) => specSetOf(line) === key).map((line) => line.id))]));
}

/** Angebot, wie es aus der Kalkulation vorgeschlagen wird. */
export function defaultQuoteOffer(calc: QuoteOfferCalc, templates: QuoteOfferTemplates): QuoteOffer {
  const optionLines = calc.groups.find((group) => group.key === 'OPTIONS')?.lines ?? [];
  const sets = defaultSpecSets(calc);
  const optionsText = groupText(templates, calc, 'OPTIONS');
  const groups = calc.groups.filter((group) => isMainGroup(group.key) && group.lines.length > 0).map((group) => defaultGroup(calc, templates, group));
  return {
    date: calc.createdAt.slice(0, 10),
    techFields: ['lengthM', 'widthM', 'waterLevelM', 'flightSpacingM', 'scrapingSpeedCmS'],
    groups,
    flow: groups.map((group) => ({ kind: 'group' as const, key: group.key })),
    total: { label: { de: 'Gesamtsumme Projektpreis', en: 'Total Project Price' }, priceMicros: null },
    showPlantPrices: true,
    logoId: null,
    options: {
      isVisible: optionLines.some((line) => line.showInOffer),
      showTotal: false,
      title: optionsText?.title ?? { de: 'Optionen', en: 'Options' },
      items: optionLines.map((line) => ({ kind: 'line' as const, lineId: line.id, isVisible: line.showInOffer, title: null, text: null })),
    },
    specs: { isVisible: Object.values(sets).some((pages) => pages.flat().length > 0), sets, cards: {} },
    terms: { ...DEFAULT_TERMS, validUntil: calc.validUntil ? calc.validUntil.slice(0, 10) : null },
  };
}

/**
 * Gespeichertes Angebot an den aktuellen Stand der Kalkulation anpassen:
 * gelöschte Positionen fallen heraus, neue kommen hinten dazu (sichtbar wie in
 * der Kalkulation eingestellt), Spezifikationen folgen ihrer Anlage. Ohne
 * gespeichertes Angebot gilt der Vorschlag.
 */
export function reconcileQuoteOffer(saved: QuoteOffer | null, calc: QuoteOfferCalc, templates: QuoteOfferTemplates): QuoteOffer {
  const proposal = defaultQuoteOffer(calc, templates);
  if (!saved) return proposal;
  const linesById = new Map(calc.groups.flatMap((group) => group.lines.map((line) => [line.id, line] as const)));

  const groups: QuoteOfferGroup[] = [];
  const savedByKey = new Map(saved.groups.map((group) => [group.key, group]));
  // Reihenfolge des gespeicherten Angebots, neue Gruppen hinten.
  const keys = [...saved.groups.map((group) => group.key), ...proposal.groups.map((group) => group.key)].filter((key, index, all) => all.indexOf(key) === index);
  for (const key of keys) {
    const calcGroup = calc.groups.find((group) => group.key === key);
    if (!calcGroup || !isMainGroup(key)) continue;
    const kept = savedByKey.get(key);
    const fresh = proposal.groups.find((group) => group.key === key);
    if (!fresh) continue; // Gruppe ohne Positionen
    if (!kept) {
      groups.push(fresh);
      continue;
    }
    const known = new Set(kept.lines.map((line) => line.lineId));
    groups.push({
      ...kept,
      lines: [
        // Eigene Positionen des Angebots bleiben, die der Kalkulation nur, solange es sie gibt.
        ...kept.lines.filter((line) => line.isCustom || calcGroup.lines.some((calcLine) => calcLine.id === line.lineId)),
        ...fresh.lines.filter((line) => !known.has(line.lineId)),
      ],
    });
  }
  const flow = reconcileFlow(saved.flow, groups);
  const order = flowGroupOrder(flow);
  groups.sort((a, b) => order.get(a.key)! - order.get(b.key)!);

  const optionIds = new Set((calc.groups.find((group) => group.key === 'OPTIONS')?.lines ?? []).map((line) => line.id));
  const knownOptions = new Set(saved.options.items.flatMap((item) => (item.kind === 'line' ? [item.lineId] : [])));
  const items = [
    ...saved.options.items.filter((item) => item.kind !== 'line' || optionIds.has(item.lineId)),
    ...proposal.options.items.filter((item) => item.kind === 'line' && !knownOptions.has(item.lineId)),
  ];

  // Spezifikationen: gespeicherte Sätze (oder der alte einzelne Satz) behalten, jede
  // Position in den Satz ihrer Anlage, neue hinten an, gelöschte heraus.
  const savedSets: Record<string, string[][]> = saved.specs.sets ?? (saved.specs.pages ? { [calc.plants[0]?.id ?? OFFER_PROJECT_SPEC_SET]: saved.specs.pages } : {});
  const sets: Record<string, string[][]> = {};
  const setKeys = Object.keys(proposal.specs.sets ?? {});
  // Eigene Einträge: Satz einer gelöschten Anlage → erste Anlage.
  const extras: Record<string, OfferSpecExtra> = Object.fromEntries(
    Object.entries(saved.specs.extras ?? {}).map(([id, extra]) => [id, setKeys.includes(extra.setKey) ? extra : { ...extra, setKey: setKeys[0] ?? OFFER_PROJECT_SPEC_SET }]),
  );
  for (const key of setKeys) {
    const belongs = (id: string) => {
      const line = linesById.get(id);
      return line ? specSetOf(line) === key : extras[id]?.setKey === key;
    };
    const pages = (savedSets[key] ?? [[]]).map((page) => page.filter(belongs));
    const placed = new Set(pages.flat());
    const moved = Object.entries(savedSets).filter(([other]) => other !== key).flatMap(([, other]) => other.flat()).filter((id) => belongs(id) && !placed.has(id));
    const unplaced = Object.keys(extras).filter((id) => extras[id]!.setKey === key && !placed.has(id) && !moved.includes(id));
    const added = [...moved, ...(proposal.specs.sets?.[key] ?? []).flat().filter((id) => !placed.has(id) && !moved.includes(id)), ...unplaced];
    if (added.length) pages[pages.length - 1] = [...pages[pages.length - 1]!, ...added];
    sets[key] = pages.length ? pages : [[]];
  }
  const cards = Object.fromEntries(Object.entries(saved.specs.cards).filter(([id]) => linesById.has(id)));
  const { pages: _legacy, ...specs } = saved.specs;

  return {
    ...saved,
    showPlantPrices: saved.showPlantPrices ?? true,
    logoId: saved.logoId ?? null,
    groups,
    flow,
    options: { ...saved.options, items },
    specs: { ...specs, sets, cards, extras },
  };
}

/**
 * Reihenfolge der Angebotsseite an die Gruppen anpassen: Gruppen ohne Eintrag kommen
 * ans Ende, Einträge verschwundener Gruppen fallen weg, Bausteine bleiben. Ohne
 * gespeicherte Reihenfolge gilt die der Gruppen.
 */
function reconcileFlow(saved: readonly OfferFlowItem[] | undefined, groups: readonly QuoteOfferGroup[]): OfferFlowItem[] {
  const keys = new Set(groups.map((group) => group.key));
  const kept = (saved ?? []).filter((item, index, all) => item.kind !== 'group' || (keys.has(item.key) && all.findIndex((other) => other.kind === 'group' && other.key === item.key) === index));
  const placed = new Set(kept.flatMap((item) => (item.kind === 'group' ? [item.key] : [])));
  return [...kept, ...groups.filter((group) => !placed.has(group.key)).map((group) => ({ kind: 'group' as const, key: group.key }))];
}

const flowGroupOrder = (flow: readonly OfferFlowItem[]) => new Map(flow.flatMap((item) => (item.kind === 'group' ? [item.key] : [])).map((key, index) => [key, index]));

/**
 * Position im Angebot verschieben — nur unter den Positionen derselben Anlage
 * (bzw. „ganzes Angebot"); die Plätze der anderen Anlagen bleiben, wo sie sind.
 * `beforeLineId` = vor diese Position, `null` = ans Ende. Gibt die neue Reihenfolge zurück.
 */
export function moveQuoteOfferLine<T extends { lineId: string }>(
  lines: readonly T[],
  plantOf: (lineId: string) => string | null | undefined,
  lineId: string,
  beforeLineId: string | null,
): T[] {
  const plant = plantOf(lineId) ?? null;
  const isSame = (entry: T) => (plantOf(entry.lineId) ?? null) === plant;
  const moving = lines.find((entry) => entry.lineId === lineId);
  if (!moving || beforeLineId === lineId) return [...lines];
  const subset = lines.filter((entry) => isSame(entry) && entry !== moving);
  const at = beforeLineId === null ? -1 : subset.findIndex((entry) => entry.lineId === beforeLineId);
  subset.splice(at >= 0 ? at : subset.length, 0, moving);
  let next = 0;
  return lines.map((entry) => (isSame(entry) ? subset[next++]! : entry));
}

/** Eine Stelle hoch (−1) oder runter (+1) unter den Positionen derselben Anlage. */
export function stepQuoteOfferLine<T extends { lineId: string }>(
  lines: readonly T[],
  plantOf: (lineId: string) => string | null | undefined,
  lineId: string,
  direction: -1 | 1,
): T[] {
  const plant = plantOf(lineId) ?? null;
  const ids = lines.filter((entry) => (plantOf(entry.lineId) ?? null) === plant).map((entry) => entry.lineId);
  const at = ids.indexOf(lineId);
  if (at < 0 || at + direction < 0 || at + direction >= ids.length) return [...lines];
  const before = direction === -1 ? ids[at - 1]! : (ids[at + 2] ?? null);
  return moveQuoteOfferLine(lines, plantOf, lineId, before);
}

// ---------- Druckmodell ----------

export interface QuoteOfferDocumentLine {
  lineId: string;
  quantity: string;
  unit: string;
  text: string;
  /** Seitenumbruch vor der Position (nie vor der ersten einer Gruppe). */
  breakBefore: boolean;
  /** Nur im Angebot angelegt. */
  isCustom: boolean;
}
export interface QuoteOfferDocumentGroup {
  key: string;
  title: string;
  isLumpSum: boolean;
  text: string;
  price: string | null;
  /** Angebotswert der Gruppe auf Cent (Mikro als Text), auch wenn der Preis nicht gezeigt wird. */
  amountMicros: string;
  lines: QuoteOfferDocumentLine[];
}
/** Zeile einer Preisermittlung bzw. Preisübersicht. */
export interface QuoteOfferDocumentCalcRow {
  id: string;
  op: OfferCalcRow['op'];
  label: string;
  /** „3" bei Prozent-Zeilen, sonst null. */
  percent: string | null;
  /** Summe darüber, auf die sich die Prozent beziehen. */
  before: string;
  amount: string;
}
/** Angebotsseite in Reihenfolge: Gruppen und Bausteine. `breakBefore` = neue Seite davor. */
export type QuoteOfferDocumentFlowItem =
  | { kind: 'group'; group: QuoteOfferDocumentGroup; breakBefore: boolean }
  | { kind: 'heading'; id: string; title: string; subtitle: string; breakBefore: boolean }
  | { kind: 'text'; id: string; text: string; breakBefore: boolean }
  /** `groupTitles` leer = addiert nichts (der Druck lässt sie weg, der Designer warnt). */
  | { kind: 'subtotal'; id: string; label: string; amount: string; groupTitles: string[]; groupKeys: string[]; sinceStart: boolean; breakBefore: boolean }
  | {
      kind: 'calc';
      id: string;
      title: string;
      base: { label: string; amount: string; groupCount: number };
      rows: QuoteOfferDocumentCalcRow[];
      result: { label: string; amount: string; amountMicros: string };
      breakBefore: boolean;
    };
export type QuoteOfferDocumentOption =
  | {
      kind: 'line';
      lineId: string;
      quantity: string;
      title: string;
      text: string;
      amount: string;
      isPerBasin: boolean;
      plantLabel: string | null;
      breakBefore: boolean;
      /** Nur im Angebot angelegt (Menge, Text, Preis von Hand). */
      isCustom: boolean;
      /** Menge bzw. Preis von Hand geändert — Werte der Kalkulation zum Zurücksetzen. */
      calcQuantity: string | null;
      calcAmount: string | null;
    }
  | { kind: 'heading'; id: string; title: string; breakBefore: boolean }
  | { kind: 'text'; id: string; text: string; breakBefore: boolean }
  | {
      kind: 'priceSummary';
      id: string;
      title: string;
      base: { label: string; amount: string };
      options: { lineId: string; label: string; amount: string }[];
      rows: QuoteOfferDocumentCalcRow[];
      result: { label: string; amount: string };
      /** Ausgeblendete Optionen, die nicht mitzählen (Hinweis im Designer). */
      hiddenOptionCount: number;
      breakBefore: boolean;
    };
export interface QuoteOfferDocumentSpecCard {
  kind: 'card';
  lineId: string;
  position: number;
  title: string;
  rows: { label: string; value: string; isTotal: boolean }[];
  /** Eigene Karte des Angebots (sonst Position der Kalkulation). */
  isExtra: boolean;
  /** Aus dem Artikelstamm geladen. */
  articleId: string | null;
}
export type QuoteOfferDocumentSpecItem = QuoteOfferDocumentSpecCard | { kind: 'heading'; id: string; title: string } | { kind: 'text'; id: string; text: string };
export interface QuoteOfferDocumentPlant {
  id: string;
  name: string;
  subtitle: string;
  /** `field` = Feld der Kalkulation, `rowId` = eigene Zeile; `calcValue` = Wert der Kalkulation, wenn von Hand geändert. */
  tech: { label: string; value: string; rowId: string | null; field: OfferTechField | null; calcValue: string | null }[];
  /** Gruppen der Anlage in der Reihenfolge der Seite. */
  groups: QuoteOfferDocumentGroup[];
  /** Seite in Reihenfolge, mit Bausteinen (bei einer Anlage auch die Gruppen „ganzes Angebot"). */
  flow: QuoteOfferDocumentFlowItem[];
  /** Preis der Anlage (nur bei mehreren Anlagen und „Preis je Anlage zeigen"). */
  price: string | null;
  /** Spezifikationsseiten der Anlage, auch leere (der Designer zeigt sie, der Druck lässt sie weg). */
  specPages: QuoteOfferDocumentSpecItem[][];
}
export interface QuoteOfferDocument {
  language: OfferLanguage;
  labels: (typeof OFFER_LABELS)[OfferLanguage];
  number: string;
  date: string;
  title: string;
  /** Mehr als eine Anlage: eigene Seite je Anlage plus Zusammenfassung. */
  isMulti: boolean;
  plants: QuoteOfferDocumentPlant[];
  /** Gruppen „ganzes Angebot" (Doku, Fracht …) — bei einer Anlage auf deren Seite, sonst in der Zusammenfassung. */
  projectGroups: QuoteOfferDocumentGroup[];
  /** Preise je Anlage in der Zusammenfassung; null = nur Gesamtpreis. */
  plantPrices: { label: string; amount: string }[] | null;
  total: { label: string; amount: string; amountMicros: string; breakBefore: boolean };
  options: { title: string; items: QuoteOfferDocumentOption[]; total: string | null } | null;
  /** Spezifikationen der Gruppen „ganzes Angebot". */
  projectSpecPages: QuoteOfferDocumentSpecItem[][];
  /** Feste (`key` = OfferTermKey) und eigene Konditionen (`key` = ID) in Reihenfolge, ausgeblendete fehlen. */
  terms: { key: string; label: string; value: string; isCustom: boolean }[];
  logo: OfferLogoLayout;
}

const quantityFormat = new Intl.NumberFormat('de-DE', { maximumFractionDigits: 2 });
const decimalFormat = new Intl.NumberFormat('de-DE', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const euroFormat = new Intl.NumberFormat('de-DE', { maximumFractionDigits: 0 });
const centFormat = new Intl.NumberFormat('de-DE', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

export const formatOfferQuantity = (value: number): string => quantityFormat.format(Math.round(value * 100) / 100);

/** „€ 131.200" — volle Euro ohne Nachkommastellen, sonst mit Cent (wie die alten Angebote); negativ „€ −4.648,20". */
export function formatOfferMoney(micros: bigint): string {
  const cents = roundToCents(micros) / 10_000n;
  const abs = cents < 0n ? -cents : cents;
  const text = abs % 100n === 0n ? euroFormat.format(Number(abs / 100n)) : centFormat.format(Number(abs) / 100);
  return `€ ${cents < 0n ? '−' : ''}${text}`;
}

/** „07.10.26" */
export function formatOfferDate(iso: string): string {
  const [year, month, day] = iso.slice(0, 10).split('-');
  return `${day}.${month}.${year!.slice(2)}`;
}

// Paare aus den base44-Artikeln (Einheit DE → EN), z. B. „paar" → „set" bei der Gleichlaufüberwachung.
const UNIT_EN: Record<string, string> = { stk: 'pcs', 'stk.': 'pcs', stück: 'pcs', pc: 'pcs', satz: 'set', paar: 'set', pauschal: 'lump sum', 'pausch.': 'lump sum', psch: 'lump sum', ls: 'lump sum', std: 'h', 'std.': 'h' };
const UNIT_DE: Record<string, string> = { pcs: 'Stk.', pc: 'Stk.', stk: 'Stk.', stück: 'Stk.', set: 'Satz', 'lump sum': 'psch', ls: 'psch' };

/** Einheit im Angebot je Sprache (die Kalkulation speichert sie, wie der Artikel sie hat). */
export function offerUnit(unit: string, language: OfferLanguage): string {
  const key = unit.trim().toLowerCase();
  return (language === 'en' ? UNIT_EN[key] : UNIT_DE[key]) ?? unit;
}

/** Text einer Position im Angebot: Angebotstext des Artikels, sonst sein Name. */
export function offerLineText(line: QuoteCalcLine, language: OfferLanguage): string {
  return line.offerText?.[language]?.trim() || line.articleName || line.category;
}

/** Datenblatt-Titel: Titel aus dem Artikel, sonst der Mengenformel zur Kategorie, sonst die Kategorie. */
export function offerSpecTitle(line: QuoteCalcLine, templates: QuoteOfferTemplates, language: OfferLanguage): string {
  const own = line.specTitle?.[language]?.trim();
  if (own) return own;
  return templates.formulas.find((row) => row.category === line.category)?.specTitle[language]?.trim() || line.category;
}

/** Beschreibung einer Option: die Merkmal-Zeilen des Artikels (so hat die alte App sie geführt). */
export function offerOptionText(line: QuoteCalcLine, language: OfferLanguage): string {
  return (line.specs?.[language] ?? []).map((row) => [row.label, row.value].filter(Boolean).join(' ')).join('\n');
}

/**
 * Gesamtmenge einer Position im Angebot: Menge der Kalkulation × Becken ihrer Anlage
 * (bzw. aller Anlagen); einmal gezählte Gruppen und pauschale Optionen einmal. Auf 2 Stellen.
 */
export function offerTotalQuantity(result: QuoteCalcResult, lineId: string): number {
  const line = result.groups.flatMap((entry) => entry.lines).find((entry) => entry.id === lineId);
  return line ? Math.round(line.quantity * line.factor * 100) / 100 : 0;
}

/** Das Angebot als Druckmodell — eine Wahrheit für die Vorschau und das PDF. */
export function buildQuoteOfferDocument(
  calc: QuoteOfferCalc,
  result: QuoteCalcResult,
  offer: QuoteOffer,
  language: OfferLanguage,
  templates: QuoteOfferTemplates,
): QuoteOfferDocument {
  const pick = (text: LocalizedText) => text[language];
  const lines = new Map(calc.groups.flatMap((group) => group.lines.map((line) => [line.id, { line, group }] as const)));
  const lineResults = new Map(result.groups.flatMap((group) => group.lines.map((line) => [line.id, line] as const)));
  const offerQuantity = (lineId: string, override: number | null | undefined) => override ?? offerTotalQuantity(result, lineId);
  const overrides = new Map(offer.groups.flatMap((group) => group.lines.map((entry) => [entry.lineId, entry] as const)));
  const isMulti = calc.plants.length > 1;
  const labels = OFFER_LABELS[language];
  type Entry = QuoteOfferGroup['lines'][number];
  /** Anlage einer Position im Angebot; eigene Positionen tragen sie selbst. */
  const plantOfEntry = (entry: Entry) => (entry.isCustom ? (entry.plantId ?? null) : (lineResults.get(entry.lineId)?.plantId ?? null));
  const exists = (entry: Entry) => entry.isCustom || lines.has(entry.lineId);

  /** Gruppen des Angebots, gefiltert auf die Positionen einer Anlage (null = ganzes Angebot). */
  const groupsFor = (plantId: string | null): QuoteOfferDocumentGroup[] =>
    offer.groups.flatMap((group): QuoteOfferDocumentGroup[] => {
      if (!group.isVisible) return [];
      const own = group.lines.filter((entry) => exists(entry) && plantOfEntry(entry) === plantId);
      if (!own.length) return [];
      const visible = own.filter((entry) => entry.isVisible);
      if (!group.isLumpSum && !visible.length) return [];
      const amount = roundToCents(own.reduce((sum, entry) => sum + (entry.isCustom ? 0n : (lineResults.get(entry.lineId)?.totalAmounts.offerMicros ?? 0n)), 0n));
      return [{
        key: group.key,
        title: pick(group.title),
        isLumpSum: group.isLumpSum,
        text: pick(group.text).trim(),
        price: group.showPrice ? formatOfferMoney(amount) : null,
        amountMicros: amount.toString(),
        lines: group.isLumpSum
          ? []
          : visible.map((entry, index) => {
              const breakBefore = index > 0 && Boolean(entry.breakBefore);
              if (entry.isCustom) {
                return {
                  lineId: entry.lineId,
                  quantity: entry.quantity === null ? '' : formatOfferQuantity(entry.quantity),
                  unit: entry.unit ? offerUnit(entry.unit, language) : '',
                  text: entry.text ? pick(entry.text) : '',
                  breakBefore,
                  isCustom: true,
                };
              }
              const found = lines.get(entry.lineId)!;
              return {
                lineId: entry.lineId,
                quantity: formatOfferQuantity(offerQuantity(entry.lineId, entry.quantity)),
                unit: offerUnit(entry.unit || found.line.unit, language),
                text: entry.text ? pick(entry.text) : offerLineText(found.line, language),
                breakBefore,
                isCustom: false,
              };
            }),
      }];
    });

  const calcRows = (rows: readonly OfferCalcRow[], baseMicros: bigint) => {
    const computed = computeOfferCalcRows(baseMicros, rows);
    return {
      rows: rows.map((row, index): QuoteOfferDocumentCalcRow => ({
        id: row.id,
        op: row.op,
        label: pick(row.label),
        percent: row.op === 'amount' ? null : quantityFormat.format(row.percent ?? 0),
        before: formatOfferMoney(computed.rows[index]!.beforeMicros),
        amount: formatOfferMoney(computed.rows[index]!.amountMicros),
      })),
      resultMicros: computed.resultMicros,
    };
  };

  /**
   * Seite in Reihenfolge: Gruppen (soweit sie auf dieser Seite etwas zeigen) und
   * Bausteine. Ein Umbruch vor einer Gruppe, die hier nichts zeigt, geht auf das
   * Nächste über — er gehört zur Stelle, nicht zum Inhalt.
   */
  const flowFor = (groups: QuoteOfferDocumentGroup[]): QuoteOfferDocumentFlowItem[] => {
    const byKey = new Map(groups.map((group) => [group.key, group]));
    const flow: readonly OfferFlowItem[] = offer.flow ?? offer.groups.map((group) => ({ kind: 'group' as const, key: group.key }));
    const out: QuoteOfferDocumentFlowItem[] = [];
    let pendingBreak = false;
    let sinceSum: QuoteOfferDocumentGroup[] = [];
    let sinceStart = true;
    const above: QuoteOfferDocumentGroup[] = [];
    const sum = (list: QuoteOfferDocumentGroup[]) => list.reduce((total, group) => total + BigInt(group.amountMicros), 0n);
    for (const item of flow) {
      const breakBefore: boolean = pendingBreak || Boolean(item.breakBefore);
      pendingBreak = false;
      if (item.kind === 'group') {
        const group = byKey.get(item.key);
        if (!group) {
          pendingBreak = breakBefore;
          continue;
        }
        out.push({ kind: 'group', group, breakBefore });
        sinceSum.push(group);
        above.push(group);
      } else if (item.kind === 'heading') {
        out.push({ kind: 'heading', id: item.id, title: pick(item.title), subtitle: pick(item.subtitle).trim(), breakBefore });
      } else if (item.kind === 'text') {
        out.push({ kind: 'text', id: item.id, text: pick(item.text), breakBefore });
      } else if (item.kind === 'subtotal') {
        out.push({ kind: 'subtotal', id: item.id, label: pick(item.label), amount: formatOfferMoney(sum(sinceSum)), groupTitles: sinceSum.map((group) => group.title), groupKeys: sinceSum.map((group) => group.key), sinceStart, breakBefore });
        sinceSum = [];
        sinceStart = false;
      } else {
        const base = sum(above);
        const rows = calcRows(item.rows, base);
        out.push({
          kind: 'calc',
          id: item.id,
          title: pick(item.title),
          base: { label: pick(item.baseLabel), amount: formatOfferMoney(base), groupCount: above.length },
          rows: rows.rows,
          result: { label: pick(item.result), amount: formatOfferMoney(rows.resultMicros), amountMicros: rows.resultMicros.toString() },
          breakBefore,
        });
        sinceSum = [];
        sinceStart = false;
      }
    }
    return out;
  };

  /** Spezifikationsseiten eines Satzes; Pos.-Nummern laufen je Satz ab 1 (nur Karten). */
  const specPagesFor = (setKey: string, plant: QuoteCalcPlant | null): QuoteOfferDocumentSpecItem[][] => {
    if (!offer.specs.isVisible) return [];
    const pages = offer.specs.sets?.[setKey] ?? [];
    const plantResult = plant ? result.plants.find((entry) => entry.id === plant.id) : result.plants[0];
    const extras = offer.specs.extras ?? {};
    let position = 0;
    return pages.map((page) =>
      page.flatMap((id): QuoteOfferDocumentSpecItem[] => {
        const extra = extras[id];
        if (extra) {
          if (extra.kind === 'heading') return [{ kind: 'heading', id, title: pick(extra.title) }];
          if (extra.kind === 'text') return [{ kind: 'text', id, text: pick(extra.text) }];
          position += 1;
          const rows = extra.rows[language].map((row) => ({ label: row.label, value: row.value, isTotal: Boolean(row.isTotal) }));
          return [{ kind: 'card', lineId: id, position, title: pick(extra.title), rows, isExtra: true, articleId: extra.articleId }];
        }
        const found = lines.get(id);
        const card = offer.specs.cards[id];
        if (!found || card?.isHidden || overrides.get(id)?.isVisible === false) return [];
        const rows = (card?.rows?.[language] ?? found.line.specs?.[language] ?? []).map((row) => ({ label: row.label, value: row.value, isTotal: Boolean((row as { isTotal?: boolean }).isTotal) }));
        // Räumbalken: die Balkenlänge gehört aufs Datenblatt (so standen sie in den alten Angeboten).
        if (/^balken$/i.test(found.line.category.trim()) && plantResult && plantResult.variables.flightLengthM > 0) {
          rows.push({ label: labels.flightLength, value: quantityFormat.format(Math.round(plantResult.variables.flightLengthM * 1000)), isTotal: true });
        }
        const isLength = found.line.unit.trim().toLowerCase() === 'm';
        rows.push({ label: isLength ? labels.totalLength : labels.totalAmount, value: formatOfferQuantity(offerQuantity(id, overrides.get(id)?.quantity)), isTotal: true });
        position += 1;
        return [{ kind: 'card', lineId: id, position, title: card?.title ? pick(card.title) : offerSpecTitle(found.line, templates, language), rows, isExtra: false, articleId: null }];
      }),
    );
  };

  const flowOrder = new Map<string, number>((offer.flow ?? []).flatMap((item) => (item.kind === 'group' ? [item.key as string] : [])).map((key, index) => [key, index]));
  const inFlowOrder = (groups: QuoteOfferDocumentGroup[]) =>
    flowOrder.size ? [...groups].sort((a, b) => (flowOrder.get(a.key) ?? 999) - (flowOrder.get(b.key) ?? 999)) : groups;
  const projectGroups = inFlowOrder(groupsFor(null));
  const pricing = result.pricing?.plants ?? [];
  const showPlantPrices = isMulti && (offer.showPlantPrices ?? true);
  const techRows = (offer.techRows ?? []).filter((row) => pick(row.label).trim() || row.value.trim());
  const plants = calc.plants.map((plant): QuoteOfferDocumentPlant => {
    const price = pricing.find((entry) => entry.id === plant.id);
    const groups = inFlowOrder(groupsFor(plant.id));
    const subtitle = offer.plantSubtitles?.[plant.id] ?? plant.subtitle;
    return {
      id: plant.id,
      name: plant.name,
      subtitle: pick(subtitle),
      tech: [
        ...offer.techFields.map((field) => {
          const calcValue = field === 'basinCount' ? String(plant.basinCount) : decimalFormat.format(plant[field]);
          const own = offer.techValues?.[field];
          const ownLabel = offer.techLabels?.[field]?.[language]?.trim();
          return { label: ownLabel || OFFER_TECH_LABELS[field][language], value: own ?? calcValue, rowId: null, field, calcValue: own !== undefined && own !== calcValue ? calcValue : null };
        }),
        ...techRows.map((row) => ({ label: pick(row.label), value: row.value, rowId: row.id, field: null, calcValue: null })),
      ],
      groups,
      // Bei einer Anlage stehen die Gruppen „ganzes Angebot" mit auf der Seite, in der Reihenfolge des Angebots.
      flow: flowFor(isMulti ? groups : [...groups, ...projectGroups]),
      price: showPlantPrices && price ? formatOfferMoney(price.priceMicros) : null,
      specPages: specPagesFor(plant.id, plant),
    };
  });

  const calculated = result.withoutOptions.allBasins.offerMicros;
  const price = offer.total.priceMicros ?? calc.finalPriceMicros;
  const totalMicros = roundToCents(price !== null ? BigInt(price) : calculated);

  let options: QuoteOfferDocument['options'] = null;
  if (offer.options.isVisible) {
    let sum = 0n;
    const shown: { lineId: string; label: string; amountMicros: bigint }[] = [];
    let hiddenOptionCount = 0;
    const optionLine = (item: Extract<QuoteOfferOptionItem, { kind: 'line' }>) => {
      const found = lines.get(item.lineId);
      const lineResult = lineResults.get(item.lineId);
      if (!found || !lineResult) return null;
      const calcAmount = roundToCents(lineResult.totalAmounts.offerMicros);
      return { found, lineResult, calcAmount, amount: item.amountMicros != null ? roundToCents(BigInt(item.amountMicros)) : calcAmount };
    };
    for (const item of offer.options.items) {
      if (item.kind === 'custom') {
        if (item.isVisible) shown.push({ lineId: item.id, label: pick(item.title), amountMicros: roundToCents(BigInt(item.amountMicros)) });
        else hiddenOptionCount += 1;
        continue;
      }
      if (item.kind !== 'line') continue;
      const option = optionLine(item);
      if (!option) continue;
      if (item.isVisible) shown.push({ lineId: item.lineId, label: item.title ? pick(item.title) : offerLineText(option.found.line, language), amountMicros: option.amount });
      else hiddenOptionCount += 1;
    }
    const items = offer.options.items.flatMap((item): QuoteOfferDocumentOption[] => {
      if (!item.isVisible) return [];
      const breakBefore = Boolean(item.breakBefore);
      if (item.kind === 'heading') return [{ kind: 'heading', id: item.id, title: pick(item.title), breakBefore }];
      if (item.kind === 'text') return [{ kind: 'text', id: item.id, text: pick(item.text), breakBefore }];
      if (item.kind === 'priceSummary') {
        const base = totalMicros + shown.reduce((total, entry) => total + entry.amountMicros, 0n);
        const rows = calcRows(item.rows, base);
        return [{
          kind: 'priceSummary',
          id: item.id,
          title: pick(item.title),
          base: { label: pick(item.baseLabel), amount: formatOfferMoney(totalMicros) },
          options: shown.map((entry) => ({ lineId: entry.lineId, label: entry.label, amount: formatOfferMoney(entry.amountMicros) })),
          rows: rows.rows,
          result: { label: pick(item.result), amount: formatOfferMoney(rows.resultMicros) },
          hiddenOptionCount,
          breakBefore,
        }];
      }
      if (item.kind === 'custom') {
        const amount = roundToCents(BigInt(item.amountMicros));
        sum += amount;
        return [{
          kind: 'line',
          lineId: item.id,
          quantity: item.quantity,
          title: pick(item.title),
          text: pick(item.text),
          amount: formatOfferMoney(amount),
          isPerBasin: false,
          plantLabel: null,
          breakBefore,
          isCustom: true,
          calcQuantity: null,
          calcAmount: null,
        }];
      }
      const option = optionLine(item);
      if (!option) return [];
      const { found, lineResult, amount, calcAmount } = option;
      sum += amount;
      const plant = isMulti && lineResult.plantId ? calc.plants.find((entry) => entry.id === lineResult.plantId) : undefined;
      const calcQuantity = `${formatOfferQuantity(lineResult.quantity * lineResult.factor)} ${offerUnit(found.line.unit, language)}`;
      const ownQuantity = item.quantity?.trim();
      return [{
        kind: 'line',
        lineId: item.lineId,
        quantity: ownQuantity || calcQuantity,
        title: item.title ? pick(item.title) : offerLineText(found.line, language),
        text: item.text ? pick(item.text) : offerOptionText(found.line, language),
        amount: formatOfferMoney(amount),
        isPerBasin: found.line.isPerBasin,
        plantLabel: plant ? pick(plant.subtitle) || plant.name : null,
        breakBefore,
        isCustom: false,
        calcQuantity: ownQuantity && ownQuantity !== calcQuantity ? calcQuantity : null,
        calcAmount: amount !== calcAmount ? formatOfferMoney(calcAmount) : null,
      }];
    });
    if (items.length) options = { title: pick(offer.options.title), items, total: offer.options.showTotal ? formatOfferMoney(sum) : null };
  }

  const hidden = new Set(offer.terms.hidden ?? []);
  const fixed = OFFER_TERM_KEYS.filter((key) => !hidden.has(key)).map((key) => ({
    key: key as string,
    label: OFFER_TERM_LABELS[key][language],
    value: key === 'validUntil' ? (offer.terms.validUntil ? formatOfferDate(offer.terms.validUntil) : '') : pick(offer.terms[key]),
    isCustom: false,
  }));
  const custom = (offer.terms.custom ?? []).map((term) => ({ key: term.id, label: pick(term.label), value: pick(term.value), isCustom: true }));
  const termOrder = new Map((offer.terms.order ?? []).map((key, index) => [key, index]));
  const terms = [...fixed, ...custom]
    .map((term, index) => ({ term, rank: termOrder.get(term.key) ?? 1000 + index }))
    .sort((a, b) => a.rank - b.rank)
    .map(({ term }) => term);

  return {
    language,
    labels,
    number: calc.displayNumber,
    date: formatOfferDate(offer.date),
    title: (offer.title ? pick(offer.title).trim() : '') || calc.name,
    isMulti,
    plants,
    projectGroups,
    plantPrices: showPlantPrices ? plants.map((plant) => ({ label: plant.subtitle || plant.name, amount: plant.price ?? '' })) : null,
    total: { label: pick(offer.total.label), amount: formatOfferMoney(totalMicros), amountMicros: totalMicros.toString(), breakBefore: Boolean(offer.totalBreakBefore) },
    options,
    projectSpecPages: specPagesFor(OFFER_PROJECT_SPEC_SET, null),
    terms,
    logo: offer.logo ?? DEFAULT_OFFER_LOGO,
  };
}
